/**
 * `usePasswordRefusalText()` — the ONE sentence a Colony web app shows when a server refuses a
 * password under the NIST SP 800-63B-4 policy (L-R7CLOSE row 39; adopted from UMB lane L16's hook).
 *
 * Every server answers a policy refusal as HTTP 400 `PASSWORD_POLICY_VIOLATION` with
 * `details.failed` naming the requirements (`@puffin-core/password-policy`'s `passwordRefusal`).
 * The English `message` beside them is for logs; a person sees THIS, in their language, naming what
 * failed — which matters because the server knows things a checklist may not (the person's own
 * names, on a form that never asked for them).
 *
 * It lives in auth-shell, beside the checklist, because login-shell and invite-core both sit above
 * this package; a hook in login-shell would put the change card behind a dependency cycle.
 *
 * @module @puffin-core/auth-shell/hooks/usePasswordRefusalText
 */

import { useCallback } from 'react';
import { PASSWORD_POLICY, passwordPolicyFailures } from '@puffin-core/password-policy';

import { useAuthShellTranslate } from './useAuthShellTranslate.js';

/**
 * @returns {(error: {errorCode?: string, errorDetails?: Object}|null|undefined) => string|null}
 *   given an API failure in the web clients' shape (`@puffin-core/api-client`'s ApiError: the
 *   server's code on `errorCode`, its details on `errorDetails`), the translated refusal naming
 *   each failed requirement — or `null` when it is not a password-policy refusal, so the caller
 *   shows its own message
 */
export function usePasswordRefusalText() {
  const t = useAuthShellTranslate();
  return useCallback(
    (error) => {
      const failed = passwordPolicyFailures(
        error ? { code: error.errorCode, details: error.errorDetails } : null,
      );
      if (!failed || failed.length === 0) return null;
      const requirements = failed
        .map((id) =>
          t(`authShell.password.requirement.${id}`, {
            min: PASSWORD_POLICY.minLength,
            max: PASSWORD_POLICY.maxBytes,
          }),
        )
        .join('; ');
      return t('authShell.password.refused', { requirements });
    },
    [t],
  );
}

export default usePasswordRefusalText;
